import path from "node:path";
import type { ChannelAdapter, ChannelRuntime, OutboundMessage } from "@mewbleh/nori-plugin-sdk";
import qrcode from "qrcode-terminal";

type SocketHandle = { socket: any; stop(): Promise<void> };

async function createSocket(runtime: ChannelRuntime): Promise<SocketHandle> {
  const baileys: any = await import("baileys");
  const authDir = String(runtime.config.authDir ?? path.resolve(".nori/credentials/whatsapp", runtime.accountId));
  const { state, saveCreds } = await baileys.useMultiFileAuthState(authDir);
  const socket = baileys.default({ auth: state, markOnlineOnConnect: false, syncFullHistory: false, generateHighQualityLinkPreview: false });
  socket.ev.on("creds.update", saveCreds);
  socket.ev.on("connection.update", (update: any) => {
    if (update.qr) qrcode.generate(update.qr, { small: true });
    if (update.connection === "open") runtime.log("info", "WhatsApp channel connected");
    if (update.connection === "close") runtime.log("warn", "WhatsApp channel disconnected", update.lastDisconnect);
  });
  socket.ev.on("messages.upsert", ({ messages, type }: any) => {
    if (type !== "notify") return;
    void Promise.all(
      messages.map(async (message: any) => {
        if (!message.message || message.key.fromMe) return;
        const jid = String(message.key.remoteJid ?? "");
        const sender = String(message.key.participant ?? jid);
        const content = message.message;
        const text = String(
          content.conversation ??
            content.extendedTextMessage?.text ??
            content.imageMessage?.caption ??
            content.videoMessage?.caption ??
            "",
        );
        const mediaNode =
          content.imageMessage ?? content.videoMessage ?? content.audioMessage ?? content.documentMessage ?? content.stickerMessage;
        let media: Array<{ data: Uint8Array; mediaType: string; name?: string }> | undefined;
        if (mediaNode) {
          try {
            const data = await baileys.downloadMediaMessage(message, "buffer", {});
            media = [{
              data,
              mediaType: String(mediaNode.mimetype ?? "application/octet-stream"),
              ...(mediaNode.fileName ? { name: String(mediaNode.fileName) } : {}),
            }];
          } catch (error) {
            runtime.log("warn", "Unable to download WhatsApp attachment", { message: (error as Error).message });
          }
        }
        await runtime.onMessage({
          id: String(message.key.id),
          conversation: {
            channel: "whatsapp",
            accountId: runtime.accountId,
            id: jid,
            kind: jid.endsWith("@g.us") ? "group" : "dm",
          },
          sender: { id: sender, ...(message.pushName ? { displayName: String(message.pushName) } : {}) },
          text,
          ...(media?.length ? { media } : {}),
          ...(content.extendedTextMessage?.contextInfo?.stanzaId
            ? { replyToId: String(content.extendedTextMessage.contextInfo.stanzaId) }
            : {}),
          mentioned: (content.extendedTextMessage?.contextInfo?.mentionedJid?.length ?? 0) > 0,
          receivedAt: Number(message.messageTimestamp ?? Math.floor(Date.now() / 1000)) * 1000,
        });
      }),
    ).catch((error) => runtime.log("error", "WhatsApp message failed", { message: (error as Error).message }));
  });
  const abort = () => socket.end(new Error("Nori stopping"));
  runtime.signal.addEventListener("abort", abort, { once: true });
  return {
    socket,
    stop: async () => {
      runtime.signal.removeEventListener("abort", abort);
      socket.end(new Error("Nori stopping"));
    },
  };
}

export class WhatsAppChannel implements ChannelAdapter {
  readonly id = "whatsapp";
  readonly capabilities = { media: true, reactions: true, threads: false, groups: true, login: "qr" as const };
  readonly #sockets = new Map<string, any>();

  async start(runtime: ChannelRuntime): Promise<{ stop(): Promise<void> }> {
    const handle = await createSocket(runtime);
    this.#sockets.set(runtime.accountId, handle.socket);
    return {
      stop: async () => {
        this.#sockets.delete(runtime.accountId);
        await handle.stop();
      },
    };
  }

  async send(message: OutboundMessage, runtime: Omit<ChannelRuntime, "onMessage">): Promise<{ messageId: string }> {
    const socket = this.#sockets.get(runtime.accountId);
    if (!socket) throw new Error(`WhatsApp account is not connected: ${runtime.accountId}`);
    const attachment = message.media?.[0];
    const source = attachment?.data ? Buffer.from(attachment.data) : attachment?.url ? { url: attachment.url } : undefined;
    const payload = !attachment || !source
      ? { text: message.text }
      : attachment.mediaType.startsWith("image/")
        ? { image: source, caption: message.text }
        : attachment.mediaType.startsWith("video/")
          ? { video: source, caption: message.text, mimetype: attachment.mediaType }
          : attachment.mediaType.startsWith("audio/")
            ? { audio: source, mimetype: attachment.mediaType }
            : { document: source, mimetype: attachment.mediaType, fileName: attachment.name ?? "attachment", caption: message.text };
    const sent = await socket.sendMessage(message.conversation.id, payload);
    return { messageId: String(sent.key.id) };
  }

  async probe(config: Record<string, unknown>): Promise<{ ok: boolean; message?: string }> {
    const authDir = String(config.authDir ?? "");
    return authDir ? { ok: true, message: `Auth directory: ${authDir}` } : { ok: false, message: "WhatsApp auth directory is not configured" };
  }

  async login(config: Record<string, unknown>): Promise<void> {
    if (!config.authDir) throw new Error("WhatsApp login requires authDir");
  }

  async logout(config: Record<string, unknown>): Promise<void> {
    const socket = this.#sockets.get(String(config.accountId ?? "default"));
    if (socket) await socket.logout();
  }
}
